import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  ChevronLeft, Clock, CheckCircle, XCircle, 
  Loader2, AlertTriangle, User, Calendar,
  FileText, MessageSquare, ThumbsUp, ThumbsDown,
  Download, ExternalLink, Eye, Paperclip, History
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_JUSTIFICATIVA = {
  'atraso': { label: 'Atraso', color: 'bg-amber-500' },
  'falta': { label: 'Falta', color: 'bg-red-500' },
  'saida_antecipada': { label: 'Saída Antecipada', color: 'bg-orange-500' },
  'hora_extra': { label: 'Hora Extra', color: 'bg-blue-500' },
  'hora_extra_nao_autorizada': { label: 'HE Não Autorizada', color: 'bg-blue-500' },
  'esquecimento_registro': { label: 'Esquecimento', color: 'bg-purple-500' },
  'marcacao_impar': { label: 'Marcação Ímpar', color: 'bg-pink-500' },
  'marcacao_incompleta': { label: 'Marcação Incompleta', color: 'bg-pink-500' },
  'marcacao_duplicada': { label: 'Marcação Duplicada', color: 'bg-pink-500' },
  'interjornada_insuficiente': { label: 'Interjornada', color: 'bg-red-600' },
  'intrajornada_insuficiente': { label: 'Intrajornada', color: 'bg-red-600' },
  'jornada_excedida': { label: 'Jornada Excedida', color: 'bg-red-700' },
  'dsr_prejudicado': { label: 'DSR Prejudicado', color: 'bg-yellow-600' },
  'outros': { label: 'Outros', color: 'bg-slate-500' }
};

const STATUS_CONFIG = {
  'pendente': { label: 'Pendente', color: 'bg-amber-500', textColor: 'text-amber-500' },
  'aprovada': { label: 'Aprovada', color: 'bg-emerald-500', textColor: 'text-emerald-500' },
  'rejeitada': { label: 'Rejeitada', color: 'bg-red-500', textColor: 'text-red-500' }
};

// Componente de Detalhes Completos da Justificativa para o Gestor
const DetalheJustificativaGestor = ({ justificativa, onVoltar, onAprovar, onRejeitar, processando, jornyxToken }) => {
  const [comentario, setComentario] = useState('');
  const tipoConfig = TIPOS_JUSTIFICATIVA[justificativa.tipo_inconsistencia] || TIPOS_JUSTIFICATIVA.outros;
  const foiAutomatica = justificativa.historico?.some(h => h.acao === 'criada_automatica');
  const temMotivos = justificativa.motivos && justificativa.motivos.length > 0;

  const formatarDataHora = (dataStr) => {
    if (!dataStr) return '-';
    try {
      if (dataStr.includes('T')) {
        return new Date(dataStr).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      const [year, month, day] = dataStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      return `${diasSemana[date.getDay()]}, ${day}/${month}/${year}`;
    } catch {
      return dataStr;
    }
  };

  const abrirDocumento = (doc) => {
    if (doc.url) {
      window.open(doc.url, '_blank');
    } else {
      toast.error('URL do documento não disponível');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20" data-testid="detalhe-justificativa-gestor">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 pb-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" onClick={onVoltar} className="text-white p-2 h-auto" data-testid="btn-voltar-detalhes">
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Analisar Justificativa</h1>
            <p className="text-purple-100 text-sm">{justificativa.empregado_nome}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge className={`${tipoConfig.color} text-white`}>
              {tipoConfig.label}
            </Badge>
            {foiAutomatica && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">
                Auto-detectada
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-4 space-y-4 -mt-2">
        {/* Informações do Empregado e Inconsistência */}
        <Card className="bg-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-purple-500" />
              Dados da Inconsistência
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500 block">Empregado</span>
                <span className="font-medium text-slate-800">{justificativa.empregado_nome}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Matrícula</span>
                <span className="font-medium text-slate-800">{justificativa.empregado_matricula || '-'}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Data da Inconsistência</span>
                <span className="font-medium text-slate-800">{formatarDataHora(justificativa.data_inconsistencia)}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Tipo</span>
                <Badge className={`${tipoConfig.color} text-white`}>{tipoConfig.label}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Motivos do Empregado */}
        <Card className="bg-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-500" />
              Motivos Informados pelo Empregado ({justificativa.motivos?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!temMotivos ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Aguardando preenchimento</span>
                </div>
                <p className="text-sm text-amber-600">
                  O empregado ainda não informou o motivo desta inconsistência. 
                  Você não pode aprovar ou rejeitar até que ele preencha.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {justificativa.motivos.map((m, idx) => (
                  <div key={m.id || idx} className="bg-slate-50 rounded-lg p-4 border-l-4 border-emerald-500">
                    <p className="text-slate-800 font-medium">{m.motivo}</p>
                    {m.descricao && (
                      <p className="text-sm text-slate-600 mt-2">{m.descricao}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      Enviado em: {formatarDataHora(m.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documentos Anexados */}
        <Card className="bg-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-blue-500" />
              Documentos Anexados ({justificativa.documentos?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!justificativa.documentos || justificativa.documentos.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">
                Nenhum documento foi anexado pelo empregado
              </p>
            ) : (
              <div className="space-y-2">
                {justificativa.documentos.map((doc, idx) => (
                  <div 
                    key={doc.id || idx} 
                    className="flex items-center justify-between bg-slate-50 rounded-lg p-3 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{doc.nome || `Documento ${idx + 1}`}</p>
                        <p className="text-xs text-slate-500">
                          Enviado em: {formatarDataHora(doc.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => abrirDocumento(doc)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`btn-ver-doc-${idx}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      {doc.url && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = doc.url;
                            link.download = doc.nome || 'documento';
                            link.click();
                          }}
                          className="text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                          data-testid={`btn-download-doc-${idx}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Histórico */}
        {justificativa.historico && justificativa.historico.length > 0 && (
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Histórico
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {justificativa.historico.map((h, idx) => {
                  // Determinar texto da ação
                  let textoAcao = null;
                  if (h.acao === 'criada_automatica') textoAcao = 'Justificativa criada automaticamente pelo sistema';
                  else if (h.acao === 'criada') textoAcao = 'Justificativa criada pelo empregado';
                  else if (h.acao === 'motivo_adicionado') textoAcao = `Motivo adicionado: "${h.motivo || ''}"`;
                  else if (h.acao === 'documento_anexado') textoAcao = `Documento anexado: ${h.documento || 'arquivo'}`;
                  else if (h.acao === 'aprovada' && h.gestor_nome) textoAcao = `Aprovada por ${h.gestor_nome}`;
                  else if (h.acao === 'rejeitada' && h.gestor_nome) textoAcao = `Rejeitada por ${h.gestor_nome}`;
                  else if (h.acao === 'enviada_aprovacao') textoAcao = 'Enviada para aprovação do gestor';
                  
                  // Não renderizar se não tiver texto
                  if (!textoAcao) return null;
                  
                  return (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-slate-400 mt-2" />
                      <div>
                        <p className="text-slate-700">{textoAcao}</p>
                        <p className="text-xs text-slate-400">{formatarDataHora(h.data)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Área de Decisão */}
        <Card className="bg-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-500" />
              Sua Decisão
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!temMotivos ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">Ação bloqueada</span>
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  Aguarde o empregado preencher o motivo da inconsistência antes de aprovar ou rejeitar.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Comentário para o empregado (opcional)
                  </label>
                  <Textarea
                    placeholder="Ex: Aprovado conforme atestado apresentado..."
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    rows={3}
                    data-testid="input-comentario-gestor"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => onAprovar(justificativa.id, comentario)}
                    disabled={processando}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 h-12"
                    data-testid="btn-aprovar-detalhe"
                  >
                    {processando ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <ThumbsUp className="w-5 h-5 mr-2" />
                        Aprovar Justificativa
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => onRejeitar(justificativa.id, comentario)}
                    disabled={processando}
                    variant="destructive"
                    className="flex-1 h-12"
                    data-testid="btn-rejeitar-detalhe"
                  >
                    {processando ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <ThumbsDown className="w-5 h-5 mr-2" />
                        Rejeitar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const UnifiedAprovacoes = ({ onVoltar }) => {
  const { jornyxToken, jornyxUser } = useUnifiedAuth();
  const [justificativas, setJustificativas] = useState({ pendentes: [], processadas: [] });
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(null);
  const [justificativaDetalhe, setJustificativaDetalhe] = useState(null); // Para tela de detalhes completa
  const [activeTab, setActiveTab] = useState('pendentes');

  useEffect(() => {
    if (jornyxToken) {
      carregarJustificativas();
    }
  }, [jornyxToken]);

  const carregarJustificativas = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/app/justificativas-equipe`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setJustificativas(data);
      }
    } catch (err) {
      console.error('Erro ao carregar justificativas:', err);
      toast.error('Erro ao carregar justificativas da equipe');
    } finally {
      setLoading(false);
    }
  };

  const processarJustificativa = async (justificativaId, aprovado, comentario = '') => {
    try {
      setProcessando(justificativaId);
      
      const res = await fetch(`${API_URL}/api/app/justificativas/${justificativaId}/aprovar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          aprovado,
          comentario: comentario || null
        })
      });
      
      if (res.ok) {
        toast.success(aprovado ? 'Justificativa aprovada!' : 'Justificativa rejeitada');
        setJustificativaDetalhe(null);
        carregarJustificativas();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao processar justificativa');
      }
    } catch (err) {
      console.error('Erro ao processar:', err);
      toast.error('Erro ao processar justificativa');
    } finally {
      setProcessando(null);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '-';
    try {
      const data = new Date(dataStr);
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dataStr;
    }
  };

  // Se estiver visualizando detalhes de uma justificativa
  if (justificativaDetalhe) {
    return (
      <DetalheJustificativaGestor
        justificativa={justificativaDetalhe}
        onVoltar={() => setJustificativaDetalhe(null)}
        onAprovar={(id, comentario) => processarJustificativa(id, true, comentario)}
        onRejeitar={(id, comentario) => processarJustificativa(id, false, comentario)}
        processando={processando === justificativaDetalhe.id}
        jornyxToken={jornyxToken}
      />
    );
  }

  if (!jornyxUser?.is_gestor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-0 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
            <h2 className="text-xl font-semibold text-white mb-2">Acesso Restrito</h2>
            <p className="text-slate-400 mb-4">
              Esta área é exclusiva para gestores com subordinados cadastrados.
            </p>
            {onVoltar && (
              <Button onClick={onVoltar} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Voltar ao Menu
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 pb-6">
        <div className="flex items-center gap-3 mb-4">
          {onVoltar && (
            <Button variant="ghost" onClick={onVoltar} className="text-white p-2 h-auto">
              <ChevronLeft className="w-6 h-6" />
            </Button>
          )}
          <div>
            <h1 className="text-xl font-bold text-white">Aprovações</h1>
            <p className="text-purple-100 text-sm">Justificativas da sua equipe</p>
          </div>
        </div>
        
        {/* Contador */}
        <div className="bg-white/20 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-white" />
            <span className="text-white font-medium">Pendentes</span>
          </div>
          <Badge className="bg-white text-purple-600 font-bold">
            {justificativas.total_pendentes || 0}
          </Badge>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="px-4 -mt-2">
        <div className="bg-slate-800/50 rounded-xl p-1 flex gap-1">
          <button
            onClick={() => setActiveTab('pendentes')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'pendentes' 
                ? 'bg-purple-500 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Pendentes ({justificativas.pendentes?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('processadas')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'processadas' 
                ? 'bg-slate-600 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Processadas
          </button>
        </div>
      </div>
      
      {/* Conteúdo */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : activeTab === 'pendentes' ? (
          justificativas.pendentes?.length === 0 ? (
            <Card className="bg-white/5 border-0">
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
                <p className="text-slate-400">Nenhuma justificativa pendente</p>
                <p className="text-slate-500 text-sm mt-1">Sua equipe está em dia!</p>
              </CardContent>
            </Card>
          ) : (
            justificativas.pendentes.map((j) => {
              const tipoConfig = TIPOS_JUSTIFICATIVA[j.tipo_inconsistencia] || TIPOS_JUSTIFICATIVA[j.tipo] || TIPOS_JUSTIFICATIVA.outros;
              const foiAutomatica = j.historico?.some(h => h.acao === 'criada_automatica');
              const temMotivos = j.motivos && j.motivos.length > 0;
              const ultimoMotivo = temMotivos ? j.motivos[j.motivos.length - 1]?.motivo : null;
              
              return (
                <Card 
                  key={j.id} 
                  className="border-0 bg-white/95 hover:bg-white transition-all cursor-pointer"
                  onClick={() => setJustificativaDetalhe(j)}
                  data-testid={`aprovacao-card-${j.id}`}
                >
                  <CardContent className="p-4">
                    {/* Cabeçalho */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${tipoConfig.color} rounded-full`}>
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{j.empregado_nome}</p>
                          <p className="text-xs text-slate-500">{j.empregado_matricula}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={`${tipoConfig.color} text-white text-xs`}>
                          {tipoConfig.label}
                        </Badge>
                        {foiAutomatica && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            Auto-detectada
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Info da justificativa */}
                    <div className="bg-slate-50 rounded-lg p-3 mb-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">
                          Data: <strong>{formatarData(j.data_inconsistencia)}</strong>
                        </span>
                      </div>
                      {ultimoMotivo && (
                        <div className="flex items-start gap-2 text-sm">
                          <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5" />
                          <span className="text-slate-600 line-clamp-2">{ultimoMotivo}</span>
                        </div>
                      )}
                      {!temMotivos && (
                        <div className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                          <span className="text-amber-600 font-medium">Aguardando preenchimento pelo empregado</span>
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {j.motivos?.length || 0} motivos
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {j.documentos?.length || 0} docs
                        </span>
                      </div>
                    </div>
                    
                    {/* Botão de ação */}
                    <Button
                      className="w-full bg-purple-500 hover:bg-purple-600"
                      data-testid={`btn-analisar-${j.id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Detalhes e Analisar
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )
        ) : (
          /* Processadas */
          justificativas.processadas?.length === 0 ? (
            <Card className="bg-white/5 border-0">
              <CardContent className="py-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-400">Nenhuma justificativa processada</p>
              </CardContent>
            </Card>
          ) : (
            justificativas.processadas.map((j) => {
              const tipoConfig = TIPOS_JUSTIFICATIVA[j.tipo_inconsistencia] || TIPOS_JUSTIFICATIVA[j.tipo] || TIPOS_JUSTIFICATIVA.outros;
              const statusConfig = STATUS_CONFIG[j.status] || STATUS_CONFIG.pendente;
              
              return (
                <Card key={j.id} className="border-0 bg-white/95" data-testid={`processada-card-${j.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 ${tipoConfig.color} rounded-full`}>
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{j.empregado_nome}</p>
                          <p className="text-xs text-slate-500">{formatarData(j.data_inconsistencia)}</p>
                        </div>
                      </div>
                      <Badge className={`${statusConfig.color} text-white text-xs`}>
                        {j.status === 'aprovada' ? (
                          <CheckCircle className="w-3 h-3 mr-1" />
                        ) : (
                          <XCircle className="w-3 h-3 mr-1" />
                        )}
                        {statusConfig.label}
                      </Badge>
                    </div>
                    
                    {j.comentario_gestor && (
                      <div className="bg-slate-50 rounded-lg p-2 text-sm text-slate-600">
                        <span className="font-medium">Seu comentário:</span> {j.comentario_gestor}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )
        )}
      </div>
    </div>
  );
};

export default UnifiedAprovacoes;
